// Displays a list of cafes pulled from the database

import React, { useEffect, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useStyles } from "./buildingListViewStyles";
import { appInsightsEvents, trackEvent, useAppStore } from "../../common";
import { BuildingSorter } from "./BuildingSorter";
import { BuildingList } from "./BuildingList";
import { OrderList } from "../OrderView/OrderList/OrderList";
import { LoadingScreen } from "../shared";
import { Divider } from "@fluentui/react-components";
import { FavoriteCafeCard } from "../FavoriteCafesView/FavoriteCafesCard";
import PrivacyLink from "../shared/PrivacyLink/PrivacyLink";
import { WebAgentToggleButton } from "../WebAgent/WebAgentToggleButton";
import { useWebChatStyles } from "../WebChat/WebChatStyles";

export const BuildingListView = () => {
  const styles = useStyles();
  const webChatStyles = useWebChatStyles();
  const navigate = useNavigate();
  const appInsights = useAppStore((state) => state.appInsights);
  const context = useAppStore((state) => state.context);
  const buildings = useAppStore((state) => state.buildings);
  const favoriteCafes = useAppStore((state) => state.favoriteCafes);

  const {
    loading: loadingBuildingCafes,
    fetch: fetchBuildingCafes,
    values: buildingCafes,
  } = buildings;
  const {
    loading: loadingFavCafes,
    fetch: fetchFavCafes,
    values: favCafes,
    isInitialized: favCafesInitialized,
    setIsInitialized: setFavCafesInitialized,
  } = favoriteCafes;
  const orders = useAppStore((state) => state.orders);
  const diningAgentEnabled = useAppStore((state) => state.featureFlags.diningAgentEnabled);

  // Initializes the component
  useEffect(() => {
    fetchBuildingCafes();
    fetchFavCafes();
  }, []);

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "BuildingListView",
    });
  }, [context, appInsights]);

  useEffect(() => {
    if (!favCafesInitialized && favCafes) {
      setFavCafesInitialized(true);
      favCafes.length && navigate("/cafe/" + favCafes[0].id);
    }
  }, [favCafes]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === "Space" || event.code === "Enter") {
      trackEvent(appInsights, appInsightsEvents.Usage.Actions.NavigateToPrivacyStatement, context);
      window.open(
        "https://privacy.microsoft.com/en-US/data-privacy-notice",
        "_blank",
        "noreferrer",
      );
    }
  };
  return (
    <div className={styles.container} data-testid="buildingListView">
      <div className={styles.subcontainer}>
        {/* The /buildings route does NOT render NavigationHeader (App.tsx
            line 102–109), so this is the only home for the chat launcher
            on the landing page that `/` redirects to. Removing it strands
            users with no way to open the assistant. Other routes (e.g.
            /cafe/:cafeId, /cafe/.../station/...) get the launcher via
            NavigationHeader. */}
        <div className={webChatStyles.toggleButtonWrapper}>
          {diningAgentEnabled && <WebAgentToggleButton />}
        </div>
        {orders && orders.values && orders.values.length > 0 && (
          <OrderList orderList={orders.values} />
        )}
        {loadingFavCafes ? (
          <LoadingScreen />
        ) : (
          favCafes && favCafes.length > 0 && <FavoriteCafeCard favoriteCafes={favCafes} />
        )}
        {orders && orders.values && orders.values.length > 0 && favCafes && favCafes.length > 0 && (
          <Divider />
        )}
        <BuildingSorter />
        {loadingBuildingCafes ? (
          <LoadingScreen />
        ) : (
          buildingCafes && buildingCafes.length > 0 && <BuildingList buildings={buildingCafes} />
        )}
        <div
          aria-label="Privacy Link"
          role="link"
          onKeyDown={(event) => {
            handleKeyDown(event);
          }}
          tabIndex={0}
        >
          <PrivacyLink />
        </div>
      </div>
    </div>
  );
};
